import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { formatEther, parseUnits } from "ethers";
import CUSDABI from "../abi/CUSD.json";
import CUSDMintABI from "../abi/CUSDMint.json";
import ERC20ABI from "../abi/ERC20.json";
import { FaCoins, FaPaperPlane, FaShoppingCart, FaPlus, FaUserPlus } from "react-icons/fa";

const cusdAddress = "0x16b6eCed5e669EAA934fB4C82F0353ab371420fa";
const cusdMintAddress = "0xaE0ADC7D373cC41b32211915640175bC08e21e1B";
const tokenNames = {
  "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE": "ETH",
  "0xdAC17F958D2ee523a2206206994597C13D831ec7": "USDT",
  "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48": "USDC",
  "0x6B175474E89094C44Da98b954EedeAC495271d0F": "DAI",
  "0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599": "WBTC",
  "0x514910771AF9Ca656af840dff83E8264EcF986CA": "LINK",
  "0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984": "UNI",
  "0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9": "AAVE",
  "0x9f8F72aA9304c8B593d555F12eF6589cC3A579A2": "MKR",
  "0xc00e94Cb662C3520282E6f5717214004A7f26888": "COMP",
  "0x6B3595068778DD592e39A122f4f5a5cF09C90fE2": "SUSHI",
  "0xB8c77482e45F1F44dE1745F52C74426C631bDD52": "BNB"
};
const tokenDecimals = {
  "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE": 18, // ETH
  "0xdAC17F958D2ee523a2206206994597C13D831ec7": 6,  // USDT
  "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48": 6,  // USDC
  "0x6B175474E89094C44Da98b954EedeAC495271d0F": 18, // DAI
  "0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599": 8,  // WBTC
  "0x514910771AF9Ca656af840dff83E8264EcF986CA": 18, // LINK
  "0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984": 18, // UNI
  "0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9": 18, // AAVE
  "0x9f8F72aA9304c8B593d555F12eF6589cC3A579A2": 18, // MKR
  "0xc00e94Cb662C3520282E6f5717214004A7f26888": 18, // COMP
  "0x6B3595068778DD592e39A122f4f5a5cF09C90fE2": 18, // SUSHI
  "0xB8c77482e45F1F44dE1745F52C74426C631bDD52": 18 // BNB
};
const cusdDecimals = 18; // CUSD decimal places from contract

const CUSD = ({ provider, account }) => {
  const [cusdContract, setCusdContract] = useState(null);
  const [cusdMintContract, setCusdMintContract] = useState(null);
  const [balance, setBalance] = useState("0");
  const [transferTo, setTransferTo] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [paymentToken, setPaymentToken] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [authAmount, setAuthAmount] = useState("");
  const [mintTo, setMintTo] = useState("");
  const [mintAmount, setMintAmount] = useState("");
  const [collateralToken, setCollateralToken] = useState("");
  const [collateralAmount, setCollateralAmount] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [supportedTokens, setSupportedTokens] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!provider || !account) return;

    const connectContracts = async () => {
      const signer = await provider.getSigner();
      const cusd = new ethers.Contract(cusdAddress, CUSDABI, signer);
      const cusdMint = new ethers.Contract(cusdMintAddress, CUSDMintABI, signer);
      setCusdContract(cusd);
      setCusdMintContract(cusdMint);
      const userBalance = await cusd.balanceOf(account);
      setBalance(formatEther(userBalance));
      const authorized = await cusdMint.authorizedMinters(account);
      setIsAuthorized(authorized);

      // Fetch supported tokens
      const tokens = await cusd.getSupportedTokens();
      // Filter out CUSD address for buying
      const filteredTokens = tokens.filter(token => token !== cusdAddress);
      setSupportedTokens(filteredTokens);
    };

    connectContracts();
  }, [provider, account]);

  const handleTransfer = async () => {
    if (!cusdContract || !transferTo || !transferAmount) return;
    try {
      const tx = await cusdContract.transfer(transferTo, parseUnits(transferAmount, cusdDecimals)); // CUSD uses 18 decimals
      await tx.wait();
      alert("Transfer successful!");
      const newBalance = await cusdContract.balanceOf(account);
      setBalance(formatEther(newBalance));
    } catch (error) {
      alert(`Transfer failed: ${error.message}`);
    }
  };

  const handleBuyCUSD = async () => {
    if (!provider || !account || !cusdContract || !paymentToken || !paymentAmount) {
      setError("Please select a token and enter an amount.");
      return;
    }

    const decimals = tokenDecimals[paymentToken] || 18;
    const amountInWei = parseUnits(paymentAmount, decimals);
    if (ethers.toBigInt(amountInWei) <= 0) {
      setError("Please enter a valid positive amount.");
      return;
    }

    try {
      const signer = await provider.getSigner();

      // Handle approval for ERC20 tokens
      if (paymentToken !== "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE") {
        const tokenContract = new ethers.Contract(paymentToken, ERC20ABI, signer);
        const allowance = await tokenContract.allowance(account, cusdAddress);
        console.log("CUSD.js - Current allowance for token:", paymentToken, "is:", allowance.toString());

        if (ethers.toBigInt(allowance) < amountInWei) {
          console.log("CUSD.js - Approving token spending...");
          const approveTx = await tokenContract.approve(cusdAddress, amountInWei);
          await approveTx.wait();
          console.log("CUSD.js - Token approved for spending.");
        }
      }

      // Execute buyCUSD transaction
      const tx = await cusdContract.buyCUSD(paymentToken, amountInWei, {
        value: paymentToken === "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE" ? amountInWei : 0,
      });
      await tx.wait();
      alert("CUSD purchase successful!");
      const newBalance = await cusdContract.balanceOf(account);
      setBalance(formatEther(newBalance));
      setError("");
      setPaymentAmount("");
    } catch (err) {
      console.error("CUSD.js - Error buying CUSD:", err);
      setError("Failed to buy CUSD: " + (err.reason || err.message || err));
    }
  };

  const handleAuthorizeMinter = async () => {
    if (!cusdMintContract || !authToken || !authAmount) return;
    try {
      const decimals = tokenDecimals[authToken] || 18;
      const amountInWei = parseUnits(authAmount, decimals);

      const tx = await cusdMintContract.authorizeMinter(authToken, amountInWei, {
        value: authToken === "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE" ? amountInWei : 0,
      });
      await tx.wait();
      alert("Authorization successful! You can now mint CUSD.");
      setIsAuthorized(true);
    } catch (error) {
      alert(`Authorization failed: ${error.message}`);
    }
  };

  const handleMintCUSD = async () => {
    if (!cusdMintContract || !mintTo || !mintAmount || !collateralToken || !collateralAmount) return;
    if (!isAuthorized) {
      alert("You need to authorize as a minter first!");
      return;
    }
    try {
      const collateralDecimals = tokenDecimals[collateralToken] || 18;
      const collateralInWei = parseUnits(collateralAmount, collateralDecimals);
      const mintInWei = parseUnits(mintAmount, cusdDecimals); // CUSD uses 18 decimals

      const feePercent = await cusdMintContract.mintFeePercent();
      const feeAmount = mintInWei.mul(feePercent).div(100);

      const tx = await cusdMintContract.mintCUSD(
        mintTo,
        mintInWei,
        collateralToken,
        collateralInWei,
        { value: collateralToken === "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE" ? collateralInWei.add(feeAmount) : 0 }
      );
      await tx.wait();
      alert("CUSD minted successfully!");
      const newBalance = await cusdContract.balanceOf(account);
      setBalance(formatEther(newBalance));
    } catch (error) {
      alert(`Minting failed: ${error.message}`);
    }
  };

  return (
    <div>
      <h2 className="cusd">
        <FaCoins className="icon" /> CUSD
      </h2>
      <div className="action-box cusd">
        <h3 className="cusd">Your Balance: <span className="cusd">{balance} CUSD</span></h3>
        <p className="cusd">Minter Status: {isAuthorized ? "Authorized" : "Not Authorized"}</p>
        <div className="action-container">
          <div className="action-box cusd">
            <h3 className="cusd">Transfer CUSD</h3>
            <input className="cusd" type="text" placeholder="Recipient Address" value={transferTo} onChange={(e) => setTransferTo(e.target.value)} />
            <input className="cusd" type="number" placeholder="Amount" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} step="0.01" />
            <button className="cusd" onClick={handleTransfer}>
              <FaPaperPlane /> Transfer
            </button>
          </div>
          <div className="action-box cusd">
            <h3 className="cusd">Buy CUSD</h3>
            <select
              className="cusd"
              value={paymentToken}
              onChange={(e) => setPaymentToken(e.target.value)}
            >
              <option value="" disabled>Select a token</option>
              {supportedTokens.map((token) => (
                <option key={token} value={token}>
                  {tokenNames[token] || token}
                </option>
              ))}
            </select>
            <input
              className="cusd"
              type="number"
              placeholder="Payment Amount (e.g., 1, 0.1, 0.001)"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              step="0.01"
            />
            <button className="cusd" onClick={handleBuyCUSD}>
              <FaShoppingCart /> Buy
            </button>
            {error && <p className="cusd">{error}</p>}
          </div>
          <div className="action-box authorize-minter cusd">
            <h3 className="cusd">Authorize Minter (via CUSDMint)</h3>
            <select
              className="cusd"
              value={authToken}
              onChange={(e) => setAuthToken(e.target.value)}
            >
              <option value="" disabled>Select a token</option>
              {supportedTokens.map((token) => (
                <option key={token} value={token}>
                  {tokenNames[token] || token}
                </option>
              ))}
            </select>
            <input
              className="cusd"
              type="number"
              placeholder="Amount (covers 100k USD fee) (e.g., 1, 0.1, 0.001)"
              value={authAmount}
              onChange={(e) => setAuthAmount(e.target.value)}
              step="0.01"
            />
            <button className="cusd" onClick={handleAuthorizeMinter}>
              <FaUserPlus /> Authorize
            </button>
          </div>
          <div className="action-box mint-cusd cusd">
            <h3 className="cusd">Mint CUSD (via CUSDMint)</h3>
            <input className="cusd" type="text" placeholder="Recipient Address" value={mintTo} onChange={(e) => setMintTo(e.target.value)} />
            <input className="cusd" type="number" placeholder="CUSD Amount" value={mintAmount} onChange={(e) => setMintAmount(e.target.value)} step="0.01" />
            <select
              className="cusd"
              value={collateralToken}
              onChange={(e) => setCollateralToken(e.target.value)}
            >
              <option value="" disabled>Select a collateral token</option>
              {supportedTokens.map((token) => (
                <option key={token} value={token}>
                  {tokenNames[token] || token}
                </option>
              ))}
            </select>
            <input
              className="cusd"
              type="number"
              placeholder="Collateral Amount (e.g., 1, 0.1, 0.001)"
              value={collateralAmount}
              onChange={(e) => setCollateralAmount(e.target.value)}
              step="0.01"
            />
            <button className="cusd" onClick={handleMintCUSD}>
              <FaPlus /> Mint
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CUSD;